import { v4 as uuid } from 'uuid';

import {
    EntryTypeBlockData,
    EntryTypeData,
    EntryTypeSectionBlockSettingsData,
    EntryTypeSectionData,
    EntryTypeSectionSettingsData,
    Optional,
} from 'chaika-frontend-types/frontend-cms';
import type { TranslationsDataParams } from 'chaika-types';

import { EntryTypeDraft, EntryTypeDraftSection } from '../types';
import { dedupeTranslations, findSectionByBlockId, normalizeBlock } from './entryTypeDraftData';

interface SectionCreateParams extends Partial<Omit<EntryTypeSectionData, 'blocks'>> {
    title: string;
}

interface SectionUpdate extends Partial<Omit<EntryTypeSectionData, 'blocks' | 'id'>> {}

interface EntryTypeUpdate extends Partial<Omit<EntryTypeData, 'sections'>> {}

export function updateEntryType(draft: EntryTypeDraft, params: EntryTypeUpdate): EntryTypeDraft {
    return { ...draft, ...params };
}

export function upsertEntryTypeTranslation(draft: EntryTypeDraft, translation: TranslationsDataParams): EntryTypeDraft {
    return { ...draft, titleTranslations: upsertTranslation(draft.titleTranslations, translation) };
}

export function addSection(draft: EntryTypeDraft, params: SectionCreateParams): EntryTypeDraft {
    const section: EntryTypeDraftSection = { id: uuid(), collapsible: false, blocks: [], ...params };

    return { ...draft, sections: [...draft.sections, section] };
}

export function removeSection(draft: EntryTypeDraft, sectionId: string): EntryTypeDraft {
    return { ...draft, sections: draft.sections.filter(section => section.id !== sectionId) };
}

export function changeSectionPosition(draft: EntryTypeDraft, oldPosition: number, newPosition: number): EntryTypeDraft {
    return { ...draft, sections: swapPositions(draft.sections, oldPosition, newPosition) };
}

export function updateSection(draft: EntryTypeDraft, sectionId: string, params: SectionUpdate): EntryTypeDraft {
    return updateSectionById(draft, sectionId, section => ({ ...section, ...params }));
}

export function updateSectionSettings(
    draft: EntryTypeDraft,
    sectionId: string,
    params: Partial<EntryTypeSectionSettingsData>,
): EntryTypeDraft {
    return updateSectionById(draft, sectionId, section => ({
        ...section,
        settings: { ...section.settings, ...params },
    }));
}

export function upsertSectionTranslation(
    draft: EntryTypeDraft,
    sectionId: string,
    translation: TranslationsDataParams,
): EntryTypeDraft {
    return updateSectionById(draft, sectionId, section => ({
        ...section,
        titleTranslations: upsertTranslation(section.titleTranslations, translation),
    }));
}

export function addBlock(draft: EntryTypeDraft, sectionId: string, block: EntryTypeBlockData): EntryTypeDraft {
    return updateSectionById(draft, sectionId, section => ({
        ...section,
        blocks: [...section.blocks, normalizeBlock(block)],
    }));
}

export function removeBlock(draft: EntryTypeDraft, sectionId: string, blockId: string): EntryTypeDraft {
    return updateSectionById(draft, sectionId, section => ({
        ...section,
        blocks: section.blocks.filter(block => block.id !== blockId),
    }));
}

export function changeBlockPosition(
    draft: EntryTypeDraft,
    blockId: string,
    oldPosition: number,
    newPosition: number,
): EntryTypeDraft {
    const sectionWithBlock = findSectionByBlockId(draft, blockId);

    if (!sectionWithBlock) {
        return draft;
    }

    return updateSectionById(draft, sectionWithBlock.id, section => ({
        ...section,
        blocks: swapPositions(section.blocks, oldPosition, newPosition),
    }));
}

export function updateBlockSettings(
    draft: EntryTypeDraft,
    blockId: string,
    blockSettingsParams: Partial<EntryTypeSectionBlockSettingsData>,
): EntryTypeDraft {
    const sectionWithBlock = findSectionByBlockId(draft, blockId);

    if (!sectionWithBlock) {
        return draft;
    }

    return updateSectionById(draft, sectionWithBlock.id, section => {
        const blockSettings = section.settings?.blockSettings ?? {};

        return {
            ...section,
            settings: {
                ...section.settings,
                blockSettings: {
                    ...blockSettings,
                    [blockId]: { ...blockSettings[blockId], ...blockSettingsParams },
                },
            },
        };
    });
}

function updateSectionById(
    draft: EntryTypeDraft,
    sectionId: string,
    update: (section: EntryTypeDraftSection) => EntryTypeDraftSection,
): EntryTypeDraft {
    return {
        ...draft,
        sections: draft.sections.map(section => (section.id === sectionId ? update(section) : section)),
    };
}

function swapPositions<T>(items: T[], oldPosition: number, newPosition: number): T[] {
    const oldIndex = oldPosition - 1;
    let newIndex = newPosition - 1;

    if (newPosition >= items.length) {
        newIndex = items.length - 1;
    } else if (newIndex < 1) {
        newIndex = 0;
    }

    const swapped = [...items];
    swapped[newIndex] = items[oldIndex];
    swapped[oldIndex] = items[newIndex];

    return swapped;
}

function upsertTranslation(
    translations: Optional<TranslationsDataParams[]>,
    translation: TranslationsDataParams,
): TranslationsDataParams[] {
    return dedupeTranslations([...(translations ?? []), translation]);
}
